/* eslint-disable jsx-a11y/click-events-have-key-events */

'use client';

import { useState } from 'react';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import { Link } from '@packages/modules/src/Link/Link';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import { Badge } from '@packages/shared/src/ui/Badge/Badge';
import type { KeyboardEvent, MouseEvent, MouseEventHandler, ReactElement, ReactNode } from 'react';
import type { Language } from '@packages/config';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Popover, PopoverContent, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import { Anchor } from '@packages/shared/src/ui/Anchor/Anchor';
import type { SlotPriority } from './types';

export type SlotProps = {
  /** Use router link or default <a> tags
   * @default true
   */
  useRouterLink?: boolean;
  /** add a language to the router link for route based i18n
   * @default undefined
   */
  language?: Language;
  /** Slot link */
  href?: string;
  /** Aria label for Slot link */
  linkAriaLabel?: string;
  /** Main icon component */
  icon: ReactElement<any>;
  /** Slot title */
  title?: string;
  /** Set badgeCount to show primary badge in the top right corner on the icon */
  badgeCount?: number;
  /** Slot popover content */
  children?: ReactNode;
  priority: SlotPriority;
  /** OnClick handler */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
};

export type PopoverHandlerEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>;

/** Base component for toolbar slots */
export const Slot = ({
  href,
  linkAriaLabel,
  icon,
  badgeCount,
  title,
  useRouterLink = true,
  language,
  children,
  priority,
  onClick,
  className,
}: SlotProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isDesktop = useMediaType();
  const config = useConfig();

  const handleOpenPopover = (event: PopoverHandlerEvent) => setAnchorEl(event.currentTarget);

  const handleClosePopover = () => setAnchorEl(null);

  const handleTogglePopover = (event: PopoverHandlerEvent) => {
    anchorEl === null ? handleOpenPopover(event) : handleClosePopover();
  };

  const localizedHref =
    href && language && !href.startsWith('http') ? localizeUrl(href, language, config) : href;
  const onSlotItemClick = (event: PopoverHandlerEvent) => {
    if (!anchorEl && children) {
      event.preventDefault();
      event.stopPropagation();
      handleTogglePopover(event);
    } else {
      handleClosePopover();
    }
  };

  const { isHydrated } = useIsHydrated();

  return (
    <Popover
      open={!!anchorEl}
      onOpenChange={(open) => {
        setAnchorEl(open ? anchorEl : null);
      }}
    >
      <div
        className="slot-item"
        onMouseEnter={isDesktop && children ? handleOpenPopover : undefined}
        onMouseLeave={isDesktop && children ? handleClosePopover : undefined}
      >
        {/* TODO: currently the "button" gets focus instead of the "anchor" when closing via escape, probably a radix thing; needs adjustment */}
        <PopoverTrigger asChild data-popoveropen={!!anchorEl}>
          <button
            type="button"
            tabIndex={-1}
            onClick={onSlotItemClick}
            onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter')
                !(e.target instanceof HTMLAnchorElement) && !href && onSlotItemClick(e);
            }}
            className={`${className ?? ''} slot-mobile-priority-${priority}`}
          >
            {/* conditionaly wrap slot in a link */}
            <ConditionalWrapper
              condition={!!href}
              wrapper={(c) =>
                !useRouterLink ? (
                  <Anchor
                    href={localizedHref}
                    onClick={onClick}
                    aria-label={linkAriaLabel ?? title}
                    className="hover:text-color-text-dark no-underline"
                  >
                    {c}
                  </Anchor>
                ) : (
                  <Link
                    lang={language}
                    href={localizedHref!}
                    locale={language}
                    onClick={onClick}
                    aria-label={linkAriaLabel ?? title}
                  >
                    {c}
                  </Link>
                )
              }
            >
              {/* content wrapper (icon and title) */}
              <div
                onClick={!isDesktop && !href ? handleTogglePopover : undefined}
                className={`${!!anchorEl && isDesktop && 'underline-indicator'} slot-icon relative flex h-full cursor-pointer flex-col items-center justify-center py-0.5 ${anchorEl ? '[&>svg]:text-color-primary' : '[&>svg]:text-color-transparent'}`}
                {...(isDesktop || !children
                  ? {}
                  : { title, 'aria-label': linkAriaLabel, role: 'button' })}
                aria-disabled={!isDesktop && !href && !isHydrated}
              >
                {/* conditionaly show primary badge at top right of the slot icon */}
                <ConditionalWrapper
                  condition={!!badgeCount}
                  wrapper={(ch) => (
                    <Badge
                      badgeContent={badgeCount}
                      badgeContentClassName="translate-x-0.5 top-0.5"
                    >
                      {ch}
                    </Badge>
                  )}
                >
                  {icon}
                </ConditionalWrapper>
                {/* optionally show title under slot icon (mobile slots do not set title) */}
                {title && (
                  <span className="hidden text-center text-[0.75rem] leading-2 hyphens-auto lg:block">
                    {title}
                  </span>
                )}
              </div>
            </ConditionalWrapper>
          </button>
        </PopoverTrigger>
        {children && anchorEl && (
          <PopoverContent
            sideOffset={isDesktop ? undefined : 4}
            className="z-modal slot-popover-content w-[15.625rem] max-w-[15.625rem] p-0 max-lg:w-dvw max-lg:max-w-dvw max-lg:overflow-visible"
          >
            {children}
          </PopoverContent>
        )}
      </div>
      {children && anchorEl && (
        <div
          // added 4px to compensate for the slight gap between the slot icon and the popover
          style={{ top: `${anchorEl.getBoundingClientRect().bottom + 4}px` }}
          className="bg-color-backdrop z-modal fixed inset-0"
        />
      )}
    </Popover>
  );
};
